import { DrawerForm, DrawerSizeEnum } from '@acceso/design-system';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ColumnContainer } from '@/components/FormFields/ColumnContainer';
import { HighlightContainer } from '@/components/FormFields/HighlightContainer';
import { InputEnrichments } from '@/components/FormFields/InputEnrichments';
import { InputFiles } from '@/components/FormFields/InputFiles';
import { InputSwitch } from '@/components/FormFields/InputSwitch';
import { InputText } from '@/components/FormFields/InputText';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { Headline } from '@/components/Headline/Headline';
import { useAudienceChannelSourceOptions } from '@/features/broadcasters/hooks/useAudienceChannelSourceOptions';
import { useChannelMediaTypeOptions } from '@/features/broadcasters/hooks/useChannelMediaTypeOptions';
import { useChannelProviderOptions } from '@/features/broadcasters/hooks/useChannelProviderOptions';
import { useChannelScopeOptions } from '@/features/broadcasters/hooks/useChannelScopeOptions';
import { useEconomicalValueChannelSourceOptions } from '@/features/broadcasters/hooks/useEconomicalValueChannelSourceOptions';
import { ChannelMediaTypeEnum, ChannelType } from '@/features/broadcasters/types';
import { useLanguageOptions } from '@/features/languages/hooks/useLanguageOptions';
import { useLocationOptions } from '@/features/locations/hooks/useLocationOptions';
import { FormTypeEnum, State } from '@/types';

import {
  DEFAULT_VALUES,
  useParentBroadcastersDrawerFormState,
} from '../../hooks/useParentBroadcastersDrawerFormState';
import { TParentChannelForm } from '../../types';

export interface ParentBroadcastersDrawerFormProps {
  isOpen: boolean;
  data?: {
    type: FormTypeEnum;
    data: ChannelType;
  } | null;
  onClose: () => void;
  onSubmit: (data: TParentChannelForm) => void;
  isMutating: boolean;
}

export const ParentBroadcastersDrawerForm = ({
  data,
  isOpen,
  onClose,
  onSubmit,
  isMutating,
}: ParentBroadcastersDrawerFormProps) => {
  const { t } = useTranslation();
  const { form } = useParentBroadcastersDrawerFormState();
  const handleSubmit = form.handleSubmit(data => {
    onSubmit(data);
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset(DEFAULT_VALUES);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!data?.data) return;

    const formData: TParentChannelForm = {
      ...data.data,
      logo: data.data.parameters?.logo_presigned_url,
    };

    if (data.type === FormTypeEnum.UPDATE) {
      form.reset(formData);
    }

    if (data.type === FormTypeEnum.DUPLICATE) {
      form.reset({
        ...formData,
        av_code: '',
        name: `${data.data.name} (Copia)`,
      });
    }
  }, [data]);

  const countryCode = useWatch({
    control: form.control,
    name: 'country_code',
  });

  const regionCode = useWatch({
    control: form.control,
    name: 'region_code',
  });

  const { countryOptions, regionOptions, subregionOptions } = useLocationOptions({
    countryCode,
    regionCode,
  });

  const channelMediaTypeOptions = useChannelMediaTypeOptions();
  const channelScopeOptions = useChannelScopeOptions();
  const languageOptions = useLanguageOptions();
  const channelProviderOptions = useChannelProviderOptions();

  const mediaType = useWatch({
    control: form.control,
    name: 'media_type',
  });
  const scope = useWatch({
    control: form.control,
    name: 'scope',
  });

  const [audienceName, setAudienceName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAudienceName('');
    }
  }, [isOpen]);

  const includeAudienceIds = useMemo(() => {
    const id = data?.data?.audience_channel_source_id ?? undefined;
    return id ? [id] : undefined;
  }, [data?.data?.audience_channel_source_id]);

  const audienceChannelSourceOptions = useAudienceChannelSourceOptions({
    mediaType,
    name: audienceName,
    includeIds: includeAudienceIds,
  });

  const economicalValueChannelSourceOptions = useEconomicalValueChannelSourceOptions({
    mediaType,
    scope,
    countryCode,
    regionCode: regionCode ?? undefined,
  });

  const isChannelRadio = mediaType === ChannelMediaTypeEnum.RADIO;

  const prevFiltersRef = useRef<{ mediaType?: unknown; scope?: unknown; countryCode?: unknown }>(
    {}
  );
  useEffect(() => {
    const prev = prevFiltersRef.current;

    if (prev.mediaType !== undefined && prev.mediaType !== mediaType) {
      form.setValue('audience_channel_source_id', null);
    }

    if (
      prev.mediaType !== undefined &&
      (prev.mediaType !== mediaType || prev.scope !== scope || prev.countryCode !== countryCode)
    ) {
      form.setValue('economical_value_channel_source_id', null);
    }

    prevFiltersRef.current = { mediaType, scope, countryCode };
  }, [mediaType, scope, countryCode, form]);

  useEffect(() => {
    if (!mediaType) {
      form.setValue('audience_channel_source_id', null);
      form.setValue('economical_value_channel_source_id', null);
      return;
    }

    if (!scope || !countryCode) {
      form.setValue('economical_value_channel_source_id', null);
    }
  }, [mediaType, scope, countryCode, form]);

  return (
    <DrawerForm
      title={
        !data?.data
          ? t('Crear nueva emisora madre')
          : data.type === FormTypeEnum.UPDATE
            ? t('Editar emisora madre')
            : t('Duplicar emisora madre')
      }
      isOpen={isOpen}
      onCancel={onClose}
      onSubmit={handleSubmit}
      size={DrawerSizeEnum.MD}
      isMutating={isMutating}
      labels={{
        submit: t('Guardar'),
        cancel: t('Cancelar'),
      }}
    >
      <InputSwitch
        form={form}
        name="state"
        states={{
          active: State.Active,
          inactive: State.Inactive,
        }}
      />
      <InputText
        form={form}
        name="name"
        required
        label={t('Nombre largo')}
        placeholder={t('Escribe aqui el nombre largo del medio')}
      />
      <RowContainer>
        <InputText
          form={form}
          name="short_name"
          required
          label={t('Nombre corto')}
          placeholder={t('Escribe aqui el nombre corto')}
        />
        <InputText
          form={form}
          name="av_code"
          label={t('ID AV (Legay)')}
          placeholder={t('Escribe aqui el ID')}
        />
      </RowContainer>

      <SelectForm
        form={form}
        name="language_id"
        label={t('Idioma')}
        placeholder="Seleccionar..."
        options={languageOptions}
      />

      <SelectForm
        form={form}
        name="media_type"
        required
        label={t('Soporte')}
        placeholder={t('Seleccionar...')}
        options={channelMediaTypeOptions}
      />

      <RowContainer>
        <InputFiles
          form={form}
          name="logo"
          label={t('Logo')}
          placeholder={t('Sube una imagen Recomendado 600px x 400px')}
          isImage
          accept="image/*"
          singleFile
        />

        <ColumnContainer>
          <SelectForm
            form={form}
            name="provider"
            label={t('Proveedor')}
            placeholder="Seleccionar..."
            options={channelProviderOptions}
          />
        </ColumnContainer>
      </RowContainer>

      <HighlightContainer>
        <RowContainer>
          <SelectForm
            form={form}
            name="scope"
            required
            label={t('Ámbito')}
            placeholder={t('Seleccionar...')}
            options={channelScopeOptions}
          />

          <SelectForm
            form={form}
            name="country_code"
            required
            label={t('País')}
            placeholder={t('Seleccionar...')}
            options={countryOptions}
          />
        </RowContainer>

        <RowContainer>
          <SelectForm
            form={form}
            name="region_code"
            label={t('Región')}
            placeholder={t('Seleccionar...')}
            options={regionOptions}
            noneOptionGender="feminine"
          />
          <SelectForm
            form={form}
            name="subregion_code"
            label={t('Subregión')}
            placeholder={t('Seleccionar...')}
            options={subregionOptions}
            noneOptionGender="feminine"
          />
        </RowContainer>

        <Headline text={t('Audiencias y tarifas')} />

        <RowContainer>
          <SelectForm
            form={form}
            name="audience_channel_source_id"
            label={t('ID audiencia')}
            placeholder={t('Seleccionar...')}
            options={audienceChannelSourceOptions}
            disabled={!mediaType}
            onFilterTextChange={setAudienceName}
            withFilter
          />

          <SelectForm
            form={form}
            name="economical_value_channel_source_id"
            label={t('ID Tarifas')}
            placeholder={t('Seleccionar...')}
            options={economicalValueChannelSourceOptions}
            disabled={!mediaType || !scope || !countryCode}
          />
        </RowContainer>
      </HighlightContainer>

      <InputEnrichments form={form} isRadio={isChannelRadio} />
    </DrawerForm>
  );
};
